import { supabase } from './supabase';
import { queryCache, CACHE_TTL } from './cache';

/** Row shape from public.users used across profile, auth, and org switching. */
export interface CachedUserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  phone_number_verified: boolean;
  profile_completed_at: string | null;
  profile_prompt_dismissed_at: string | null;
  current_organization_id: string | null;
  twitter_handle: string | null;
  twitter_prompt_dismissed_at: string | null;
  password_reset_required: boolean;
  updated_at?: string;
}

const PROFILE_CACHE_VERSION = 1;

function profileCacheKey(userId: string): string {
  return queryCache.generateKey('userProfile', { userId, v: PROFILE_CACHE_VERSION });
}

export async function fetchCachedUserProfile(userId: string): Promise<CachedUserProfile | null> {
  const cacheKey = profileCacheKey(userId);
  return queryCache.deduplicate(
    cacheKey,
    async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }
      return data as CachedUserProfile | null;
    },
    CACHE_TTL.SHORT
  );
}

export function patchCachedUserProfile(userId: string, patch: Partial<CachedUserProfile>): void {
  const cacheKey = profileCacheKey(userId);
  const existing = queryCache.get<CachedUserProfile | null>(cacheKey);
  if (existing) {
    queryCache.set(cacheKey, { ...existing, ...patch }, CACHE_TTL.SHORT);
  }
}

export function invalidateCachedUserProfile(userId: string): void {
  queryCache.invalidateByParams('userProfile', { userId, v: PROFILE_CACHE_VERSION });
}
